import { useTranslator } from "@eo-locale/react";

import { useEffect, useState } from "react";

import { useRouter } from "next/router";

import parse from "html-react-parser";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Flex,
  VStack,
  Text,
  SkeletonText,
  Contribution,
  useToast
} from "@/storybook-components";
import { buildImgixUrl, dateWithSiteLocale, scrollIntoView } from "@/lib";
import type { IdeaComments } from "@/types";
import ActionList from "./action-list";
import { ViewComment, EditComment, CreateComment } from "./comment-form";
import {
  fetchComments,
  fetchMentionSuggestions,
  fetchIdea,
  refreshOneIdea
} from "@/redux/features/brainstormers/thunk";

import { setStatus } from "@/redux/features/brainstormers/slice";
import AllAlerts from "./all-alerts";

import { getVotingStatus } from "@/helpers/ideas";

const Comments = ({
  menuList,
  status,
  comments,
  showCommentBox,
  setShowCommentBox,
  mentions,
  commentable,
  commentingDisabled
}): JSX.Element => {
  const { translate, language } = useTranslator();
  const { moderated } = useAppSelector((state) => state?.site?.site);
  const toast = useToast();
  const router = useRouter();
  const { projectId, brainstormerId } = router.query as {
    projectId: string;
    brainstormerId: string;
  };
  const { commentError, idea, brainstormer } = useAppSelector(
    (state) => state.brainstormers
  );
  const { state: brainstormerState, votingStartAt, votingEndAt } = brainstormer;
  const { project } = useAppSelector((state) => state.project);
  const { state: projectState } = project;

  const { login: profileUser, type: profileUserType } = useAppSelector(
    (state) => state.user?.registeredUser?.profile
  );
  const dispatch = useAppDispatch();
  const [commentsMenu, setCommentsMenu] = useState<Array<any>>([]);
  const [editComment, setEditComment] = useState<IdeaComments>(undefined);
  const { allowCommentsForIdeas } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );

  const commentComponent = (comment) => {
    if (editComment?.id === comment?.id) {
      return (
        <EditComment
          key={comment?.id}
          value={editComment}
          setEditComment={setEditComment}
          mentions={mentions}
        />
      );
    } else {
      return (
        <ViewComment
          key={comment?.id}
          comment={comment?.comment}
          spam={comment?.spam}
        />
      );
    }
  };

  const handleEdit = (comment) => {
    const parsedElement = parse(comment?.comment || "");

    let transformedComment = "";

    const append = (tail) => {
      if (transformedComment?.length > 0) {
        transformedComment += " " + tail;
      } else {
        transformedComment += tail;
      }
    };

    const stripLeadingAtSymbol = (text: string) => {
      return text.startsWith("@") ? text.slice(1) : text;
    };

    let isOldMentionFound = false;
    const isOldMention = (elem) => {
      if (elem?.props?.className === "mention") {
        isOldMentionFound = true;
        return true;
      }
      return false;
    };

    const withNewMentionMarkup = (mention) => {
      return `<span class="withEHQMentions">${mention}</span>`;
    };

    if (Array.isArray(parsedElement)) {
      parsedElement.forEach((item) => {
        if (isOldMention(item)) {
          append(
            withNewMentionMarkup(
              stripLeadingAtSymbol(item?.props?.children || "")
            )
          );
        } else {
          append(item);
        }
      });
    } else if (typeof parsedElement === "object") {
      if (isOldMention(parsedElement)) {
        append(
          withNewMentionMarkup(
            stripLeadingAtSymbol(parsedElement?.props?.children || "")
          )
        );
      } else {
        append(parsedElement);
      }
    } else {
      append(parsedElement);
    }

    setEditComment(
      isOldMentionFound
        ? {
            ...comment,
            comment: transformedComment?.replace(/\u00A0/g, "")?.trim()
          }
        : comment
    );
  };

  useEffect(() => {
    const refreshIdea = () => {
      dispatch(fetchComments({ projectId, brainstormerId, ideaId: idea?.id }));
      dispatch(
        fetchMentionSuggestions({ projectId, brainstormerId, ideaId: idea?.id })
      );
      dispatch(fetchIdea({ projectId, brainstormerId, ideaId: idea?.id }));
      dispatch(refreshOneIdea({ ideaId: idea?.id }));
    };
    if (status === "commentSaved" && !commentError) {
      setShowCommentBox(false);
      toast({
        id: "comment-toast",
        description: translate(`brainstormers.thanksForCommenting`),
        status: "success",
        isClosable: true
      });
      refreshIdea();
    }
    if (status === "commentDeleted" && !commentError) {
      refreshIdea();
    }
    if (
      status === "commentError" &&
      commentError &&
      !toast.isActive("idea-toast")
    ) {
      toast({
        status: "error",
        id: "idea-toast",
        title: translate("errors.somethingWentWrong"),
        isClosable: true
      });
      dispatch(setStatus("idle"));
    }
  }, [
    commentError,
    status,
    projectId,
    brainstormerId,
    dispatch,
    idea?.id,
    setShowCommentBox,
    toast,
    translate
  ]);

  useEffect(() => {
    if (showCommentBox) {
      scrollIntoView("#ehq-ideas-comment-box");
      setEditComment(undefined);
    }
    if (editComment) {
      scrollIntoView(`#ehq-ideas-comment-${editComment?.id}`);
    }
  }, [showCommentBox, editComment]);

  useEffect(() => {
    setCommentsMenu(
      menuList.map((item) => {
        if (item?.type === "edit" || item?.type === "delete") {
          return {
            ...item,
            isItemVisible: commentable && !commentingDisabled,
            actionHandler: (comment) => {
              if (showCommentBox) {
                setShowCommentBox(false);
              }
              handleEdit(comment);
            },
            isDisabled: commentingDisabled
          };
        } else {
          return item;
        }
      })
    );
  }, [menuList, commentable, commentingDisabled]);

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const hasActionOptions = (comment: {
    [key: string]: string | boolean | number;
  }) => {
    const isLoggedInUser = comment.author === profileUser;
    const isMatchingUserType = comment.userType === profileUserType;
    return (
      moderated ||
      (isLoggedInUser &&
        isMatchingUserType &&
        !isVotingAllowed &&
        projectState !== "archived" &&
        brainstormerState !== "archived")
    );
  };

  return (
    <VStack w="full" spacing={9}>
      <VStack w="full" spacing={6}>
        <Flex w="full">
          <SkeletonText isLoaded={status !== "commentsFetching"} noOfLines={1}>
            {status !== "commentsFetching" && (
              <Text fontWeight="semibold">{translate("common.comments")}</Text>
            )}
          </SkeletonText>
        </Flex>

        {allowCommentsForIdeas && comments?.length > 0 && (
          <AllAlerts showVotingAlert={false} />
        )}

        {showCommentBox && !editComment && (
          <CreateComment
            mentions={mentions}
            setShowCommentBox={setShowCommentBox}
          />
        )}
      </VStack>

      {comments && comments.length > 0 ? (
        <VStack sx={{ ">:last-child": { mb: 9 } }} spacing={9} w="full">
          {comments &&
            comments.map((comment, index) => (
              <Contribution
                id={`ehq-ideas-comment-${comment?.id}`}
                key={index}
                cardVariant="unstyled"
                username={comment && comment.author}
                contributionTime={dateWithSiteLocale(
                  comment && comment.createdAt,
                  language.toLowerCase()
                )}
                userAvatarSrc={buildImgixUrl(comment?.avatar, 40, 40, "min")}
                showPin={false}
                showDescription
                showDetailedText
                variant="unstyled"
                isLoaded={status !== "commentsFetching"}
                ariaLabelVerticalDotsIcon={translate("common.more")}
                cardDescription={commentComponent(comment)}
                showMoreOptions={hasActionOptions(comment)}
                actionList={
                  <ActionList menuList={commentsMenu} currentData={comment} />
                }
              />
            ))}
        </VStack>
      ) : (
        <VStack w="full">
          <Flex w="full">
            <SkeletonText
              isLoaded={status !== "commentsFetching"}
              noOfLines={1}
            >
              {status !== "commentsFetching" && (
                <Text fontSize="sm" lineHeight="tall">
                  {translate("common.noComments")}
                </Text>
              )}
            </SkeletonText>
          </Flex>
        </VStack>
      )}
    </VStack>
  );
};

export default Comments;
